"use client";

import { AiOutlineMenu } from "react-icons/ai";
import { FaUserCircle } from "react-icons/fa";
import { BiUserCircle } from "react-icons/bi";
import { signOut } from "next-auth/react";
import { useRouter } from "next/navigation";

import useLoginModal from "@/app/hooks/useLoginModal";
import useRegisterModal from "@/app/hooks/useRegisterModal";
import useRentModal from "@/app/hooks/useRentModal";
import { SafeChat, SimplifiedUser } from "@/app/types";
import MenuItem from "./MenuItem";
import { useNotifications } from "@/app/context/NotificationContext";
import { io } from "socket.io-client";

interface UserMenuProps {
  currentUser?: SimplifiedUser | null;
  isOpen: boolean;
  toggleOpen: () => void;
  chatsData: {
    chats: SafeChat[] | null;
    totalPages: number;
    currentPage: number;
  };
}

type MenuItemType = {
  label: string;
  path?: string;
  data?: object;
  style?: string;
  className?: string;
  onClick?: () => void;
};

// const socket = io(process.env.NEXT_PUBLIC_SOCKET_URL);

const UserMenu: React.FC<UserMenuProps> = ({
  currentUser,
  isOpen,
  toggleOpen,
  chatsData,
}) => {
  const router = useRouter();
  const loginModal = useLoginModal();
  const registerModal = useRegisterModal();
  const rentModal = useRentModal();
  const { unreadCount } = useNotifications();
  // const [localUnreadCount, setLocalUnreadCount] = useState<number>(unreadCount);

  const handleMessagesClick = () => {
    // chatsData.chats?.forEach(chat => {
    //   if (chat.receiverId === currentUser?.id) {
    //     markChatAsRead(chat.id);
    //   }
    // });
    const userData = { type: currentUser?.userType };
    router.push(`/chats?userData=${encodeURIComponent(JSON.stringify(userData))}&chatType=PROPERTY&showArchived=false`);
    toggleOpen();
  };

  const handleSignOut = () => {
    const callbackUrl = process.env.NEXTAUTH_URL;
    signOut({ callbackUrl });
  };

  const handleMenuItemClick = (action: () => void) => {
    action();
    toggleOpen();
  };

  const createUrlWithParams = (path: string, data: object) =>
    `${path}?userData=${encodeURIComponent(JSON.stringify(data))}`;

  const userData = {
    type: currentUser?.userType,
  };

  const menuItems: MenuItemType[] = currentUser
    ? [
      ...(currentUser?.userType === "Diver"
        ? [
          {
            label: "Edit profile",
            path: `/editprofile/${currentUser?.id}`,
            data: userData,
          },
          {
            label: "My trips",
            path: `/trips`,
            data: userData,
          },
          {
            label: "My favorites",
            path: "/favorites",
            data: userData
          },
          {
            label: "Messages",
            onClick: handleMessagesClick,
          },
          ...(currentUser?.role === "ADMIN"
            ? [
              {
                label: "Reservations management",
                path: "/adm/reservations",
                style: "bg-gray-300",
              },
              {
                label: "Payouts",
                path: "/adm/payouts",
                style: "bg-gray-300",
              },
              {
                label: "Centers management",
                path: "/adm/center-management",
                style: "bg-gray-300",
              },
              {
                label: "Coupons",
                path: "/adm/coupons",
                style: "bg-gray-300",
              },
              {
                label: "Users count",
                path: "/adm/count",
                style: "bg-gray-300",
              }
            ]
            : []),
        ]
        : []),

      ...(currentUser?.userType === "Center"
        ? [
          {
            label: "Edit profile",
            path: `/editprofile/${currentUser?.id}`,
            data: userData,
          },
          {
            label: "My profile",
            path: `/dive-center/${currentUser?.diveCenter?.id}`,
            data: userData,
          },
          { label: "My reservations", path: "/reservations", data: userData },
          { label: "My listings", path: "/courses", data: userData },
          {
            label: "Messages",
            onClick: handleMessagesClick,
          },
          {
            label: "Payments & Payouts",
            path: `/payments-payouts/${currentUser?.diveCenter?.id}`,
            data: userData,
          },
          {
            label: "List your courses",
            onClick: rentModal.onOpen,
            style: "bg-cyan-700",
          },
        ]
        : []),

      { label: "Logout", onClick: handleSignOut },
    ]
    : [
      { label: "Login", onClick: loginModal.onOpen },
      { label: "Sign up", onClick: registerModal.onOpen },
      {
        label: "Register as a DIVE CENTER",
        onClick: () => router.push("/registercenter"),
        className: "text-cyan-600 text-justify md:hidden",
      },
    ];

  return (
    <div className="relative">
      <div className="flex flex-row items-center gap-3">
        <div className="relative">
          <div
            onClick={toggleOpen}
            className="p-2 md:py-2 md:px-3 border-[1px] border-neutral-200 flex flex-row items-center 
              gap-3 rounded-lg cursor-pointer hover:shadow-md transition"
          >
            {!currentUser ? <BiUserCircle size={20} /> : <FaUserCircle size={20} />}
            <div className="hidden md:block text-sm">{currentUser?.name}</div>
            <div className="sm:hidden">
              <AiOutlineMenu size={20} />
            </div>
          </div>
           {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-600 rounded-full flex items-center justify-center text-white text-xs">
              {unreadCount}
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 rounded-xl shadow-md w-[90vw] sm:w-[70vw] md:w-[50vw] lg:w-[30vw] 
          bg-white overflow-hidden right-0 top-12 text-sm">
          <div className="flex flex-col cursor-pointer">
            {menuItems.map((item, index) => (
              <div key={index} className={item.className || item.style || ""}>
                {item.label === "Logout" && <hr className="border-t border-black" />}
                <MenuItem
                  label={item.label}
                  onClick={() =>
                    item.onClick
                      ? handleMenuItemClick(item.onClick)
                      : handleMenuItemClick(() => {
                        if (item.data) {
                          router.push(createUrlWithParams(item.path!, item.data));
                        } else {
                          router.push(item.path!);
                        }
                      })
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;